import { type CreatePriceValidationErrorInfo, PriceStatus } from '@/api';
import TableCore from '@/components/table/table-core';
import {
  NUMERIC_FILTER_COLUMNS,
  PERCENTAGE_COLUMNS,
  computeBatchUpdate,
  exportToExcel,
  filterNonDistributorPricelistRows,
  getFontColor,
  hasCurrencyDecimalsForAll,
  hasPriceChange,
  isCellChanged,
  isCellEditable as isCellEditableFromTableHelpers,
} from '@/components/table/table-helpers';
import {
  type BaseRowDataForDisplay,
  type EditableField,
  LockableField,
} from '@/components/table/table-types';
import { FilterStatus } from '@/components/table/toolbar/filter-status';
import { VerticalSeparator } from '@/components/table/toolbar/vertical-separator';
import { useFlashCell } from '@/hooks/use-flash-cell';
import { useMissingDecimalsNotification } from '@/hooks/use-missing-decimals-notification';
import { useNotification } from '@/hooks/use-notification';
import ActionsDropdown from '@/pages/price-planning/simulation/simulation-tool-page/components/toolbar/actions-dropdown';
import { connectColors, themeColors } from '@/theme/colors';
import { LP_MIN_THRESHOLD } from '@/utils/lp-validation';
import { Button, Icon, InlineAlert } from '@lego/connect-components-react';
import { Flex, Text } from '@lego/klik-ui';
import {
  type MRT_Cell,
  type MRT_ColumnFiltersState,
  MRT_GlobalFilterTextField,
  type MRT_RowSelectionState,
  MRT_ShowHideColumnsButton,
  type MRT_TableInstance,
  MRT_ToggleFiltersButton,
  MRT_ToggleFullScreenButton,
} from 'material-react-table';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import LockDropdown from '../../../../components/table/toolbar/lock-dropdown';
import { columnGroups, getColumnData } from '../create-price-column-config';
import {
  buildSessionSaveRequest,
  calculatePricingField,
  formatDataForDisplay,
  getModifiedRowsCategorized,
  getModifiedRowsForSession,
  isApproveCreatePriceMode,
  isPriceStatusChangedFromPreliminary,
  shouldHighlightColumn,
} from '../create-price-helpers';
import { useSaveCreatePriceSession } from '../create-price-queries';
import type {
  CreatePriceModalFilters,
  CreatePriceMode,
} from '../create-price-search-params';
import type {
  DownloadOption,
  EditableFieldKey,
  RowData,
  RowDataForDisplay,
  TableColumn,
} from '../types/create-price-table-types';
import { useCreatePriceSave } from '../use-create-price-save';
import PriceDownloadModal from './price-download-modal';
import PriceStatusSelect from './price-status-select';
import { SaveConfirmationModal } from './save-confirmation-modal';

type CreatePriceTableProps = {
  initialData: Map<string, RowData>;
  originalData?: Map<string, RowData>;
  validFrom: Date;
  mode: CreatePriceMode;
  filters: CreatePriceModalFilters;
  isPublished?: boolean;
};

const CreatePriceTable = ({
  initialData,
  originalData,
  validFrom,
  mode,
  filters,
  isPublished = false,
}: CreatePriceTableProps) => {
  const isApproveMode = isApproveCreatePriceMode(mode);
  const showNotification = useNotification();
  const showMissingDecimalsError = useMissingDecimalsNotification();
  const [lockedColumn, setLockedColumn] = useState<LockableField | undefined>(
    undefined,
  );

  const [data, setData] = useState<Map<string, RowData>>(initialData);
  const [isPublishedLocal, setIsPublishedLocal] = useState(isPublished);

  // biome-ignore lint/correctness/useExhaustiveDependencies: isPublishedLocal guards against reset race after publish
  useEffect(() => {
    setData(initialData);
    setIsPublishedLocal(isPublished);
    if (!isPublished && !isPublishedLocal) {
      setIsSaved(false);
    }
    setShowSaveModal(false);
    setValidationErrors([]);
    lastValidLpRef.current.clear();
    lastSessionRowsRef.current = JSON.stringify(
      getModifiedRowsForSession(initialData, originalData ?? initialData),
    );
  }, [initialData, originalData, isPublished]);

  const effectiveIsPublished = isPublishedLocal || isPublished;

  const [isSaved, setIsSaved] = useState(isPublished);
  const [showSaveModal, setShowSaveModal] = useState(false);
  const [validationErrors, setValidationErrors] = useState<
    CreatePriceValidationErrorInfo[]
  >([]);

  const { mutate: saveSession } = useSaveCreatePriceSession();

  const { flashingCells, flashCell: flashLpCell } = useFlashCell();
  const lastValidLpRef = useRef<Map<string, number>>(new Map());
  const lastSessionRowsRef = useRef<string | null>(null);

  const comparisonBaseline = useMemo(
    () => originalData ?? initialData,
    [originalData, initialData],
  );

  useEffect(() => {
    for (const [key, row] of initialData) {
      const baseline = comparisonBaseline.get(key);
      if (baseline && row.lp !== baseline.lp && row.lp >= LP_MIN_THRESHOLD) {
        lastValidLpRef.current.set(key, row.lp);
      }
    }
  }, [initialData, comparisonBaseline]);

  useEffect(() => {
    if (isSaved) return;

    const request = buildSessionSaveRequest(
      mode,
      filters,
      data,
      comparisonBaseline,
    );
    const sessionRows = JSON.stringify(request.modifiedRows);
    if (sessionRows === lastSessionRowsRef.current) return;

    const timeout = setTimeout(() => {
      lastSessionRowsRef.current = sessionRows;
      saveSession(request);
    }, 300);

    return () => clearTimeout(timeout);
  }, [data, mode, saveSession, isSaved, filters, comparisonBaseline]);

  const errorRowKeys = useMemo(() => {
    return new Set(
      validationErrors.map((err) => `${err.priceUnitId}-${err.productId}`),
    );
  }, [validationErrors]);

  const { categorizedChanges, totalPreliminaryCount } = useMemo(() => {
    const changes = getModifiedRowsCategorized(
      data,
      comparisonBaseline,
      isApproveMode,
    );
    let preliminaryCount = 0;
    for (const row of data.values()) {
      if (row.priceStatus === PriceStatus.Preliminary) {
        preliminaryCount += 1;
      }
    }
    return {
      categorizedChanges: changes,
      totalPreliminaryCount: preliminaryCount,
    };
  }, [data, comparisonBaseline, isApproveMode]);

  const totalModifiedCount = isApproveMode
    ? categorizedChanges.priceChangesWithApprovals.length
    : categorizedChanges.all.length;

  const [rowSelection, setRowSelection] = useState<{
    state: MRT_RowSelectionState;
    items: RowData[];
  }>({ state: {}, items: [] });
  const [columnFilters, setColumnFilters] = useState<MRT_ColumnFiltersState>(
    [],
  );
  const selectedRowsCount = rowSelection.items.length;

  const { handleSave, handlePublishAndGoToApprove, isSaving } =
    useCreatePriceSave({
      mode,
      filters,
      validFrom,
      data,
      comparisonBaseline,
      categorizedChanges,
      setData,
      setIsPublishedLocal,
      setIsSaved,
      setShowSaveModal,
      setRowSelection,
      setValidationErrors,
    });

  const formattedComparisonData = useMemo(
    () => formatDataForDisplay(comparisonBaseline),
    [comparisonBaseline],
  );

  const formattedComparisonMap = useMemo(
    () => new Map(formattedComparisonData.map((r) => [r.primaryKey, r])),
    [formattedComparisonData],
  );

  const formattedData = useMemo(
    () =>
      formatDataForDisplay(data).map((row) => {
        const changed = effectiveIsPublished
          ? (data.get(row.primaryKey)?.hasPriceChange ?? false)
          : hasPriceChange(row, formattedComparisonMap);
        return { ...row, hasPriceChange: changed ? '1' : '0' };
      }),
    [data, formattedComparisonMap, effectiveIsPublished],
  );

  const cellBackgroundColor = useCallback(
    (row: RowDataForDisplay, columnKey: string) => {
      const rowKey = `${row.priceUnitId}-${row.productId}`;
      if (errorRowKeys.has(rowKey)) {
        return connectColors.core.red[100];
      }
      if (effectiveIsPublished && row.hasPriceChange === '1') {
        return themeColors.interactive.primary.subtle.selected;
      }
      if (
        !effectiveIsPublished &&
        shouldHighlightColumn(columnKey, isApproveMode)
      ) {
        return themeColors.interactive.primary.subtle.selected;
      }
      return undefined;
    },
    [errorRowKeys, effectiveIsPublished, isApproveMode],
  );

  const columnData = useMemo(
    () => getColumnData(lockedColumn, isApproveMode, isSaved),
    [lockedColumn, isApproveMode, isSaved],
  );

  const batchUpdatePricingField = useCallback(
    (field: EditableField, value: number, isChangeProportional = false) => {
      if (isSaved) return;
      const nonDistributorRows = filterNonDistributorPricelistRows(
        rowSelection.items,
        field,
      );
      if (nonDistributorRows.length === 0) {
        showNotification({
          type: 'warning',
          title: 'No eligible rows',
          description: 'The selected rows do not support editing RRP or RM.',
        });
        return;
      }
      if (
        !hasCurrencyDecimalsForAll(nonDistributorRows, showMissingDecimalsError)
      )
        return;
      const revertedKeys = new Set<string>();

      setData((prev) => {
        const updated = computeBatchUpdate(
          prev,
          nonDistributorRows,
          field,
          value,
          isChangeProportional,
          lockedColumn,
          initialData,
          calculatePricingField,
        );

        for (const row of nonDistributorRows) {
          const updatedRow = updated.get(row.primaryKey);
          if (!updatedRow || updatedRow.lp >= LP_MIN_THRESHOLD) continue;
          const baseline = comparisonBaseline.get(row.primaryKey);
          if (baseline && baseline.lp > 0) {
            const revertLp =
              lastValidLpRef.current.get(row.primaryKey) ?? baseline.lp;
            const reverted = calculatePricingField(
              LockableField.lp,
              updatedRow,
              revertLp,
              lockedColumn,
              baseline.lp,
            );
            updated.set(row.primaryKey, reverted);
            revertedKeys.add(row.primaryKey);
          }
        }

        const editableField = field as EditableFieldKey;
        for (const row of nonDistributorRows) {
          if (revertedKeys.has(row.primaryKey)) continue;
          const updatedRow = updated.get(row.primaryKey);
          if (updatedRow) {
            const editedFields = new Set(updatedRow.editedFields ?? []);
            editedFields.add(editableField);
            updated.set(row.primaryKey, { ...updatedRow, editedFields });
          }
        }
        return updated;
      });

      if (revertedKeys.size > 0) {
        showNotification({
          type: 'warning',
          title: 'Notice',
          description: `${revertedKeys.size} row(s) were kept at their previous List Price because it must be greater than 0.`,
        });
      }
    },
    [
      lockedColumn,
      initialData,
      comparisonBaseline,
      rowSelection.items,
      showNotification,
      showMissingDecimalsError,
      isSaved,
    ],
  );

  const updatePriceStatus = useCallback(
    (rowId: string, status: PriceStatus) => {
      if (isSaved) return;
      setData((prev) => {
        const row = prev.get(rowId);
        if (!row) return prev;
        const editedFields = new Set(row.editedFields ?? []);
        editedFields.add('priceStatus');
        const newMap = new Map(prev);
        newMap.set(rowId, { ...row, priceStatus: status, editedFields });
        return newMap;
      });
    },
    [isSaved],
  );

  const handleCellEdited = useCallback(
    (updatedRow: RowData, isReverting?: boolean) => {
      if (isReverting) {
        lastValidLpRef.current.delete(updatedRow.primaryKey);
        return;
      }

      if (updatedRow.lp < LP_MIN_THRESHOLD) {
        const baseline = comparisonBaseline.get(updatedRow.primaryKey);
        if (baseline && baseline.lp > 0) {
          const revertLp =
            lastValidLpRef.current.get(updatedRow.primaryKey) ?? baseline.lp;
          flashLpCell(updatedRow.primaryKey);
          showNotification({
            type: 'warning',
            title: 'Notice',
            description:
              'The List Price was adjusted back to its previous value because it must be greater than 0.',
          });
          setData((prev) => {
            if (!prev.has(updatedRow.primaryKey)) return prev;
            const reverted = calculatePricingField(
              LockableField.lp,
              updatedRow,
              revertLp,
              lockedColumn,
              baseline.lp,
            );
            const newMap = new Map(prev);
            newMap.set(updatedRow.primaryKey, reverted);
            return newMap;
          });
          return;
        }
      }

      if (updatedRow.lp >= LP_MIN_THRESHOLD) {
        lastValidLpRef.current.set(updatedRow.primaryKey, updatedRow.lp);
      }

      setData((prev) => {
        const row = prev.get(updatedRow.primaryKey);
        if (!row) return prev;
        const editedFields = new Set(row.editedFields ?? []);
        if (updatedRow.rrp !== row.rrp) editedFields.add('rrp');
        if (updatedRow.lp !== row.lp) editedFields.add('lp');
        if (updatedRow.rm !== row.rm) editedFields.add('rm');
        if (updatedRow.priceStatus !== row.priceStatus)
          editedFields.add('priceStatus');
        if (editedFields.size === 0) return prev;
        const newMap = new Map(prev);
        newMap.set(updatedRow.primaryKey, {
          ...updatedRow,
          editedFields,
        });
        return newMap;
      });
    },
    [comparisonBaseline, flashLpCell, lockedColumn, showNotification],
  );

  const isCellEditable = useCallback(
    (columnKey: TableColumn, row: RowDataForDisplay): boolean => {
      if (isSaved) return false;
      if (isApproveMode) return false;
      return isCellEditableFromTableHelpers(
        columnKey,
        row,
        lockedColumn,
        columnData,
      );
    },
    [columnData, lockedColumn, isSaved, isApproveMode],
  );

  const getCellComponent = useCallback(
    (cell: MRT_Cell<RowDataForDisplay>, key: TableColumn) => {
      const getCellValue = () => cell.row.original[key as keyof RowData];

      const showPriceStatusSelect =
        !isSaved &&
        key === 'priceStatus' &&
        (isApproveMode ||
          isPriceStatusChangedFromPreliminary(
            cell.row.original,
            comparisonBaseline,
          ));

      if (showPriceStatusSelect) {
        const comparisonRow = comparisonBaseline.get(
          cell.row.original.primaryKey,
        );
        const currentStatus =
          data.get(cell.row.original.primaryKey)?.priceStatus ??
          comparisonRow?.priceStatus ??
          PriceStatus.Preliminary;

        return (
          <PriceStatusSelect
            value={currentStatus}
            onChange={(status) =>
              updatePriceStatus(cell.row.original.primaryKey, status)
            }
          />
        );
      }
      return (
        <Text
          className="body-xs-regular"
          isTruncated
          color={
            isCellChanged(
              cell.row.original,
              key as keyof BaseRowDataForDisplay,
              formattedComparisonMap,
            )
              ? themeColors.support.warning.default
              : getFontColor(
                  isCellEditable(key, cell.row.original)
                    ? columnData[key].columnType
                    : undefined,
                )
          }
        >
          {getCellValue()}
        </Text>
      );
    },
    [
      isSaved,
      isApproveMode,
      comparisonBaseline,
      data,
      formattedComparisonMap,
      isCellEditable,
      columnData,
      updatePriceStatus,
    ],
  );

  const [columnVisibility, setColumnVisibility] = useState<
    Record<string, boolean>
  >({});
  const [filteredRows, setFilteredRows] = useState<RowDataForDisplay[]>([]);

  const handleExportData = useCallback(
    (downloadType: DownloadOption) => {
      exportToExcel(
        downloadType,
        columnData,
        formattedData,
        filteredRows,
        columnVisibility,
        'Create price',
        'create-price.xlsx',
      );
    },
    [columnData, formattedData, columnVisibility, filteredRows],
  );

  const [showPriceDownloadModal, setShowPriceDownloadModal] = useState(false);
  const priceDownloadTriggerRef = useRef<HTMLLIElement | null>(null);
  const saveTriggerRef = useRef<HTMLButtonElement | null>(null);

  const getToolbarActions = (table: MRT_TableInstance<RowDataForDisplay>) => (
    <Flex margin="8px 16px" justifyContent="space-between">
      <Flex gap="8px" alignItems="center">
        <MRT_GlobalFilterTextField
          table={table}
          placeholder="Search"
          fullWidth
        />
        <MRT_ToggleFiltersButton table={table} />
        <MRT_ShowHideColumnsButton table={table} />
        <MRT_ToggleFullScreenButton table={table} />
        <VerticalSeparator />
        <FilterStatus table={table} />
      </Flex>
      <Flex gap="12px" alignItems="center">
        {!isSaved && !isApproveMode && (
          <LockDropdown
            value={lockedColumn}
            onChange={(selected) => {
              if (lockedColumn !== selected) setLockedColumn(selected);
              else setLockedColumn(undefined);
            }}
          />
        )}
        <ActionsDropdown
          download={{
            onClick: () => {
              setShowPriceDownloadModal(true);
              setColumnVisibility(table.getState().columnVisibility);
              setFilteredRows(
                table.getFilteredRowModel().rows.map((row) => row.original),
              );
            },
            triggerRef: priceDownloadTriggerRef,
          }}
        />
        {!isSaved ? (
          <Button
            appearance="solid"
            variant="primary"
            size="small"
            ref={saveTriggerRef}
            onClick={() => setShowSaveModal(true)}
            disabled={totalModifiedCount === 0}
          >
            {isApproveMode ? 'Approve prices' : 'Publish prices'}
            {totalModifiedCount > 0 ? ` (${totalModifiedCount})` : ''}
          </Button>
        ) : (
          <Flex alignItems="center" gap="8px" padding="0 12px">
            <Text color={connectColors.core.green[700]}>
              <Icon
                name="status-circle-check"
                size="small"
                aria-label={isApproveMode ? 'Approved' : 'Published'}
              />
            </Text>
            <Text
              className="body-sm-medium"
              color={connectColors.core.green[700]}
            >
              {isApproveMode ? 'Approved' : 'Published'}
            </Text>
          </Flex>
        )}
      </Flex>
    </Flex>
  );

  const enrichedValidationErrors = useMemo(() => {
    if (validationErrors.length === 0) return [];
    return validationErrors.map((error) => {
      const rowKey = `${error.priceUnitId}-${error.productId}`;
      const row = data.get(rowKey);
      return {
        ...error,
        productName: row?.productName ?? `Product ${error.productId}`,
        priceUnitName: row?.priceUnitName ?? `Pricelist ${error.priceUnitId}`,
      };
    });
  }, [validationErrors, data]);

  return (
    <>
      {enrichedValidationErrors.length > 0 && (
        <InlineAlert
          showIcon
          variant="negative"
          isRemovable
          onDismiss={() => setValidationErrors([])}
          style={{ margin: '0 16px 8px 16px' }}
        >
          <InlineAlert.Title>
            {enrichedValidationErrors.length}{' '}
            {enrichedValidationErrors.length === 1
              ? 'price failed'
              : 'prices failed'}{' '}
            validation
          </InlineAlert.Title>
          <InlineAlert.Content>
            <Flex direction="column" gap="4px" mt="4px">
              {enrichedValidationErrors.slice(0, 5).map((error) => (
                <Text
                  key={`${error.priceUnitId}-${error.productId}`}
                  className="body-xs-regular"
                >
                  • {error.productName} ({error.productId}) in{' '}
                  {error.priceUnitName}: {error.reason ?? 'Unknown error'}
                </Text>
              ))}
              {enrichedValidationErrors.length > 5 && (
                <Text className="body-xs-regular" color="gray.600">
                  ...and {enrichedValidationErrors.length - 5} more
                </Text>
              )}
            </Flex>
          </InlineAlert.Content>
        </InlineAlert>
      )}
      <TableCore<RowData, RowDataForDisplay, TableColumn>
        formattedData={formattedData}
        columnData={columnData}
        columnGroups={columnGroups}
        isCellEditable={isCellEditable}
        cellComponent={getCellComponent}
        initialData={initialData}
        planningYear={validFrom.getFullYear()}
        getToolbar={getToolbarActions}
        formattedInitialData={
          effectiveIsPublished ? formattedData : formattedComparisonData
        }
        calculatePricingField={calculatePricingField}
        rowSelection={rowSelection}
        setRowSelection={setRowSelection}
        data={data}
        setData={setData}
        onEdit={handleCellEdited}
        lockedColumn={lockedColumn}
        tableHeight={'calc(100vh - 209px)'}
        batchUpdatePricingField={batchUpdatePricingField}
        numericFilterColumns={NUMERIC_FILTER_COLUMNS}
        percentageColumns={PERCENTAGE_COLUMNS}
        revertBaseline={comparisonBaseline}
        columnFilters={columnFilters}
        setColumnFilters={setColumnFilters}
        bulkActions={
          isApproveMode ? (
            <Button
              aria-label="Approve"
              iconPosition="start"
              size={'small'}
              style={{ backgroundColor: 'transparent' }}
              onClick={() => {
                const preliminaryPrices = rowSelection.items.filter(
                  (row) => row.priceStatus === PriceStatus.Preliminary,
                );
                const zeroLpPrices = preliminaryPrices.filter(
                  (row) => row.lp < LP_MIN_THRESHOLD,
                );
                if (zeroLpPrices.length > 0) {
                  showNotification({
                    type: 'error',
                    title: 'Oops!',
                    description: `${zeroLpPrices.length} row(s) cannot be approved because List Price must be greater than 0.`,
                  });
                  return;
                }
                setData((prev) => {
                  const newMap = new Map(prev);
                  for (const item of preliminaryPrices) {
                    const row = newMap.get(item.primaryKey);
                    if (row) {
                      const editedFields = new Set(row.editedFields ?? []);
                      editedFields.add('priceStatus');
                      newMap.set(item.primaryKey, {
                        ...row,
                        priceStatus: PriceStatus.Approved,
                        editedFields,
                        isExemptFromFiltering: true,
                      });
                    }
                  }
                  return newMap;
                });
                setRowSelection({ state: {}, items: [] });
                showNotification({
                  title: 'Approved prices',
                  description: (
                    <b>
                      {preliminaryPrices.length} preliminary prices were
                      approved.
                    </b>
                  ),
                  type: 'info',
                  duration: 2000,
                });
              }}
              iconName="status-circle-check"
              disabled={selectedRowsCount === 0}
            >
              Approve
            </Button>
          ) : undefined
        }
        isReadOnly={isSaved}
        overrideCellBackgroundColor={(row, columnKey) => {
          if (columnKey === 'lp' && flashingCells.has(row.primaryKey)) {
            return connectColors.core.red[200];
          }
          return undefined;
        }}
        customCellBackgroundColor={cellBackgroundColor}
        pinnedColumns={[
          'mrt-row-select',
          'price-list',
          'priceUnitId',
          'priceUnitName',
          'product-data-pinned',
          'productId',
          'productName',
        ]}
      />
      <PriceDownloadModal
        isOpen={showPriceDownloadModal}
        triggerRef={priceDownloadTriggerRef}
        onClose={() => setShowPriceDownloadModal(false)}
        onSubmit={handleExportData}
      />
      <SaveConfirmationModal
        isOpen={showSaveModal}
        triggerRef={saveTriggerRef}
        onClose={() => setShowSaveModal(false)}
        onConfirm={handleSave}
        onPublishAndGoToApprove={handlePublishAndGoToApprove}
        isSaving={isSaving}
        totalUpdatedCount={totalModifiedCount}
        approvalCount={categorizedChanges.priceChangesWithApprovals.length}
        totalPreliminaryCount={totalPreliminaryCount}
        mode={mode}
      />
    </>
  );
};
export default CreatePriceTable;
